import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* ============================================================
   ░ auth/auth-login.jsx · ЭКРАН ВХОДА (прод-артефакт) ░
   ============================================================
   ✅ PRODUCTION — переносится копированием как есть.
   Импорт только из '@kit-v5' и 'react'. Данные — только через `api`
   (поверхность описана в auth-login.fixtures.js).

   ⚠ ОБРАТИ ВНИМАНИЕ (UX-конвенции, не украшательство):
   1. Текст ошибки ОДИН И ТОТ ЖЕ для «нет такой почты» и «неверный пароль».
      Не разделять и не уточнять — иначе форма превращается в перебор
      заведённых в системе людей. Бэк обязан возвращать один статус.
   2. Блокировка (`status:'locked'`) — форма и кнопка выключены, идёт
      обратный отсчёт до разблокировки; счётчик тикает локально от
      `retryAfterSec`, но истиной остаётся ответ бэка на следующей попытке.
   3. `must_change_password` — не отдельная страница и не роут: это второй
      шаг ТОГО ЖЕ экрана (владелец вошёл по одноразовому паролю).
      Назад к форме входа — только через «Выйти», без сохранения сессии.
   4. Восстановления пароля, входа по коду и 2FA здесь НЕТ сознательно —
      методов в контракте нет, ложных обещаний в UI не даём.
   5. Экран pre-auth: платформенный хром (Shell/Rail/Topbar) НЕ рендерит,
      занимает всё окно, адаптив — по ширине контейнера (@container).
   ============================================================ */
import { useState, useEffect, useMemo } from 'react';
import { Btn, Ico, OsnovaLogo, Field, InputAffix, Banner, Checklist, typograph } from '@kit-v5';
import { useT } from '../i18n/i18n';
// InputAffix/Banner спредят лишние props в рантайме, но типы кита их не объявляют
// (drift зеркала — .claude/FRONT-PORT.md); локальный typed-алиас, тело не трогаем.
const InputAffixTyped = InputAffix;
const BannerTyped = Banner;
function T(s) { return typeof s === "string" ? typograph(s) : s; }
/* Локальная предпроверка нового пароля. Серверная проверка — источник истины. */
function passwordRules(t) {
    return [
        { id: "len", label: t("auth.password_rules.len"), test: (v) => v.length >= 10 },
        { id: "mix", label: t("auth.password_rules.mix"), test: (v) => /\p{L}/u.test(v) && /\d/.test(v) },
        { id: "diff", label: t("auth.login.rule_diff"), test: (v, ctx) => !!v && v !== ctx.temp },
    ];
}
const mmss = (sec) => {
    const s = Math.max(0, Math.round(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
function PassInput({ value, onChange, disabled, invalid, placeholder, autoComplete, onEnter }) {
    const t = useT();
    const [shown, setShown] = useState(false);
    return (_jsx(InputAffixTyped, { value: value, onChange: onChange, disabled: disabled, invalid: invalid, placeholder: placeholder, type: shown ? "text" : "password", autoComplete: autoComplete, spellCheck: false, onKeyDown: (e) => { if (e.key === "Enter")
            onEnter?.(); }, prefix: _jsx("span", { className: "auth-ico", children: Ico.key }), suffix: _jsx("button", { type: "button", className: "auth-peek" + (shown ? " is-on" : ""), disabled: disabled, onClick: () => setShown((s) => !s), "aria-label": t(shown ? "auth.password.peek_hide" : "auth.password.peek_show"), children: Ico.eye }) }));
}
export default function LoginScreen({ api, onSuccess }) {
    const t = useT();
    const [brand, setBrand] = useState(null);
    const [stage, setStage] = useState("signin"); // signin | 2fa | newpass | done
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [next1, setNext1] = useState("");
    const [next2, setNext2] = useState("");
    const [challenge, setChallenge] = useState(null);
    const [otp, setOtp] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [lockLeft, setLockLeft] = useState(0); // секунды до разблокировки
    const [user, setUser] = useState(null);
    useEffect(() => {
        let alive = true;
        api.getBranding().then((b) => alive && setBrand(b), () => alive && setBrand({}) // брендинг не критичен — вход не блокируем
        );
        return () => { alive = false; };
    }, [api]);
    useEffect(() => {
        if (lockLeft <= 0)
            return;
        const timer = setInterval(() => setLockLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
        return () => clearInterval(timer);
    }, [lockLeft > 0]);
    const locked = lockLeft > 0;
    const canSubmit = !!email.trim() && !!password && !busy && !locked;
    async function submitSignin() {
        if (!canSubmit)
            return;
        setBusy(true);
        setError(null);
        try {
            const r = await api.login({ email: email.trim(), password });
            if (r.status === "ok") {
                setUser(r.user);
                setStage("done");
                onSuccess?.(r.user);
            }
            else if (r.status === "two_factor") {
                setChallenge(r.challengeToken);
                setStage("2fa");
                setOtp("");
                setUseBackup(false);
                setPassword("");
            }
            else if (r.status === "must_change_password") {
                setChallenge(r.challengeToken);
                setStage("newpass");
                setNext1("");
                setNext2("");
            }
            else if (r.status === "locked") {
                setLockLeft(r.retryAfterSec);
                setPassword("");
            }
            else {
                setError({
                    tone: "danger", title: t("auth.login.err_credentials"),
                    body: r.attemptsLeft != null && r.attemptsLeft <= 3
                        ? t("auth.login.attempts_warning", { count: r.attemptsLeft })
                        : null,
                });
                setPassword("");
            }
        }
        catch (e) {
            setError({ tone: "danger", title: t("auth.common.network_error_title"), body: t("auth.common.network_error_body"), retry: true });
        }
        finally {
            setBusy(false);
        }
    }
    const rules = useMemo(() => passwordRules(t), [t]);
    const ruleCtx = { temp: password };
    const ruleState = useMemo(() => Object.fromEntries(rules.map((r) => [r.id, r.test(next1, ruleCtx)])), [rules, next1, password]);
    const rulesOk = rules.every((r) => ruleState[r.id]);
    const matchErr = next2 && next1 !== next2 ? t("auth.password.mismatch") : null;
    async function submitNewPass() {
        if (!rulesOk || matchErr || !next2 || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            const r = await api.changePassword({ challengeToken: challenge, password: next1 });
            if (r.status === "ok") {
                setUser(r.user);
                setStage("done");
                onSuccess?.(r.user);
            }
            else
                setError({ tone: "danger", title: t("auth.password.rejected_title"), body: r.message });
        }
        catch (e) {
            setError({ tone: "danger", title: t("auth.login.save_password_failed"), body: t("auth.common.network_error_body") });
        }
        finally {
            setBusy(false);
        }
    }
    function backToSignin() {
        setStage("signin");
        setChallenge(null);
        setPassword("");
        setNext1("");
        setNext2("");
        setOtp("");
        setUseBackup(false);
        setError(null);
    }
    /* второй шаг: код из приложения или резервный код */
    const otpReady = useBackup ? otp.trim().length >= 8 : otp.replace(/\D/g, "").length === 6;
    async function submitOtp() {
        if (!otpReady || busy || locked)
            return;
        setBusy(true);
        setError(null);
        try {
            const r = await api.verifyTwoFactor({ challengeToken: challenge, code: otp.trim(), backup: useBackup });
            if (r.status === "ok") {
                setUser(r.user);
                setStage("done");
                onSuccess?.(r.user);
            }
            else if (r.status === "locked") {
                setLockLeft(r.retryAfterSec);
                setOtp("");
            }
            else if (r.status === "challenge_expired") {
                backToSignin();
                setError({ tone: "danger", title: t("err.identity.challenge_expired") });
            }
            else {
                setError({
                    tone: "danger",
                    title: r.code === "identity.code_invalid"
                        ? t("err.identity.code_invalid")
                        : t(useBackup ? "auth.login.err_2fa_backup" : "auth.login.err_2fa_code"),
                });
                setOtp("");
            }
        }
        catch (e) {
            setError({ tone: "danger", title: t("auth.common.network_error_title"), body: t("auth.common.network_error_body"), retry: true });
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "auth-root", children: _jsxs("div", { className: "auth-layout", children: [_jsxs("aside", { className: "auth-brand", children: [_jsx("div", { className: "auth-brand-top", children: _jsx(OsnovaLogo, { size: 24 }) }), _jsx("div", { className: "auth-brand-mid", children: _jsx("h1", { children: brand?.tagline ? T(brand.tagline) : t("auth.login.tagline_default") }) }), _jsxs("div", { className: "auth-brand-foot", children: [_jsx("span", { className: "auth-tenant", children: T(brand?.tenant || "") }), _jsx("span", { className: "auth-ver", children: brand?.version || "" })] })] }), _jsx("main", { className: "auth-pane", children: _jsxs("div", { className: "auth-form", children: [_jsx("div", { className: "auth-brand-compact", children: _jsx(OsnovaLogo, { size: 22 }) }), stage === "signin" && (_jsxs(_Fragment, { children: [_jsx("header", { className: "auth-head", children: _jsx("h2", { children: t("auth.login.heading") }) }), locked && (_jsx(BannerTyped, { tone: "warn", title: t("auth.login.locked_title"), body: t("auth.login.locked_body", { time: mmss(lockLeft) }) })), !locked && error && (_jsx(BannerTyped, { tone: error.tone, title: error.title, body: error.body ?? undefined, action: error.retry ? { label: t("auth.common.retry"), onClick: submitSignin } : undefined })), _jsxs("div", { className: "auth-fields", children: [_jsx(Field, { label: t("auth.login.email_label"), children: _jsx(InputAffixTyped, { value: email, onChange: setEmail, disabled: busy || locked, invalid: !!error && !error.retry, placeholder: "name@company.ru", type: "email", autoComplete: "username", spellCheck: false, onKeyDown: (e) => { if (e.key === "Enter")
                                                        submitSignin(); }, prefix: _jsx("span", { className: "auth-ico", children: Ico.mail }) }) }), _jsx(Field, { label: t("auth.login.password_label"), children: _jsx(PassInput, { value: password, onChange: setPassword, disabled: busy || locked, invalid: !!error && !error.retry, placeholder: t("auth.login.password_placeholder"), autoComplete: "current-password", onEnter: submitSignin }) })] }), _jsx(Btn, { kind: "primary", size: "lg", loading: busy, disabled: !canSubmit, onClick: submitSignin, children: locked ? t("auth.login.locked_button", { time: mmss(lockLeft) }) : t("auth.login.submit") }), _jsxs("p", { className: "auth-foot-note", children: [t("auth.login.support_note"), " ", _jsx("a", { href: `mailto:${brand?.supportEmail || "support@osnova.ru"}`, children: brand?.supportEmail || "support@osnova.ru" })] })] })), stage === "2fa" && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "auth-head", children: [_jsx("h2", { children: t("auth.login.twofactor_heading") }), _jsx("p", { children: t(useBackup ? "auth.login.twofactor_subtitle_backup" : "auth.login.twofactor_subtitle_app") })] }), locked && (_jsx(BannerTyped, { tone: "warn", title: t("auth.login.locked_title"), body: t("auth.login.locked_body", { time: mmss(lockLeft) }) })), !locked && error && (_jsx(BannerTyped, { tone: error.tone, title: error.title, body: error.body ?? undefined, action: error.retry ? { label: t("auth.common.retry"), onClick: submitOtp } : undefined })), _jsx("div", { className: "auth-fields", children: _jsx(Field, { label: t(useBackup ? "auth.login.twofactor_backup_label" : "auth.login.twofactor_code_label"), children: _jsx(InputAffixTyped, { value: otp, disabled: busy || locked, invalid: !!error && !error.retry, onChange: (v) => setOtp(useBackup
                                                    ? String(v).toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 9)
                                                    : String(v).replace(/\D/g, "").slice(0, 6)), placeholder: t(useBackup ? "auth.login.twofactor_backup_placeholder" : "auth.login.twofactor_code_placeholder"), inputMode: useBackup ? "text" : "numeric", autoComplete: "one-time-code", spellCheck: false, onKeyDown: (e) => { if (e.key === "Enter")
                                                    submitOtp(); }, prefix: _jsx("span", { className: "auth-ico", children: Ico.key }) }) }) }), _jsx("button", { type: "button", className: "auth-link", onClick: () => { setUseBackup((b) => !b); setOtp(""); setError(null); }, children: t(useBackup ? "auth.login.twofactor_use_app" : "auth.login.twofactor_use_backup") }), _jsx(Btn, { kind: "primary", size: "lg", loading: busy, disabled: !otpReady || busy || locked, onClick: submitOtp, children: locked ? t("auth.login.locked_button", { time: mmss(lockLeft) }) : t("auth.login.submit") }), _jsx("button", { type: "button", className: "auth-link", onClick: backToSignin, children: t("auth.common.logout") })] })), stage === "newpass" && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "auth-head", children: [_jsx("h2", { children: t("auth.login.newpass_heading") }), _jsx("p", { children: t("auth.login.newpass_subtitle") })] }), error && _jsx(BannerTyped, { tone: error.tone, title: error.title, body: error.body ?? undefined }), _jsxs("div", { className: "auth-fields", children: [_jsx(Field, { label: t("auth.password.new_label"), children: _jsx(PassInput, { value: next1, onChange: setNext1, disabled: busy, placeholder: t("auth.password.new_placeholder"), autoComplete: "new-password" }) }), _jsx(Field, { label: t("auth.password.confirm_label"), error: matchErr ?? undefined, children: _jsx(PassInput, { value: next2, onChange: setNext2, disabled: busy, invalid: !!matchErr, placeholder: t("auth.password.confirm_placeholder"), autoComplete: "new-password", onEnter: submitNewPass }) })] }), _jsx("div", { className: "auth-rules", children: _jsx(Checklist, { readOnly: true, items: rules.map(({ id, label }) => ({ id, label })), value: ruleState }) }), _jsx(Btn, { kind: "primary", size: "lg", loading: busy, disabled: !rulesOk || !!matchErr || !next2 || busy, onClick: submitNewPass, children: t("auth.password.save_submit") }), _jsx("button", { type: "button", className: "auth-link", onClick: backToSignin, children: t("auth.common.logout") })] })), stage === "done" && (_jsxs("div", { className: "auth-done", children: [_jsx("span", { className: "auth-done-ico", children: Ico.check }), _jsx("h2", { children: t("auth.login.done_heading") }), _jsx("p", { children: user?.name ? t("auth.login.done_greeting", { name: user.name }) : t("auth.login.done_generic") })] }))] }) })] }) }));
}
